/**
 * KAN-883: панель фильтров таблицы CRM - одна и та же в обычном и
 * развёрнутом на весь экран виде (состояние в адресе, не копия): период
 * (CrmPeriodControl: кнопки и календарь), исход, сортировка, кнопки быстрого выбора
 * (KAN-898: "Мусор" только владельцу, остальным в hidden),
 * атрибут, город и организация, поиск и менеджер, отметка события периода.
 * KAN-905: рядом с периодом переключатель "Только новые лиды" (NewLeadsToggle).
 * Компонент только рисует; состояние даёт CrmTableSection.
 * KAN-906: все фильтры одной компактной строкой (h-8, перенос без зазоров),
 * подписи внутри полей (aria-label на месте); trailing (число клиентов и кнопка
 * "Развернуть") в конце строки справа.
 * KAN-906: на телефоне (< md) компактно: строка периода (кнопки, прокрутка по
 * горизонтали), строка поиск + "Фильтры" (значок - число активных, lib/crmTableActiveFilters)
 * + trailing; остальное (календарь, новые лиды, исход, сортировка, быстрые фильтры,
 * атрибут, город, организация, менеджер) в раскрывающейся панели. Те же компоненты,
 * другая раскладка; на md и шире всё одной строкой.
 * KAN-963: период одной кнопкой календаря (быстрые периоды в её окне), на телефоне в
 * строке периода; без прокрутки строки, иначе окно календаря обрезается.
 */
import { useId, useState, type ReactNode } from 'react';
import { SlidersHorizontal } from 'lucide-react';
import { CrmAttributeFilter } from '@/components/crm/CrmAttributeFilter';
import { CrmLeadFilters } from '@/components/crm/CrmLeadFilters';
import { CrmLocationFilters } from '@/components/crm/CrmLocationFilters';
import { CrmPeriodControl } from '@/components/crm/CrmPeriodControl';
import { NewLeadsToggle } from '@/components/crm/NewLeadsToggle';
import type { NewLeadsOnlyState } from '@/hooks/useNewLeadsOnly';
import { CrmTableShortcuts } from '@/components/crm/CrmTableShortcuts';
import { CrmTableSortSelect } from '@/components/crm/CrmTableSortSelect';
import { SegmentedControl } from '@/components/crm/board/SegmentedControl';
import { NARROW_SCREEN_QUERY } from '@/hooks/useCrmBoardView';
import type { CrmLeadFiltersState } from '@/hooks/useCrmLeadFilters';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import type { CrmTablePeriodState } from '@/hooks/useCrmTablePeriod';
import type { CrmTableUrlFilters } from '@/hooks/useCrmTableUrlFilters';
import type { CrmAttributeFilter as AttributeFilterValue } from '@/lib/crmAttributeFilter';
import { crmTableActiveFilterCount } from '@/lib/crmTableActiveFilters';
import { CRM_TABLE_OUTCOME_OPTIONS, CRM_TABLE_PERIOD_CHOICES } from '@/lib/crmTablePeriod';
import { CRM_LEADS_EVENT_LABEL, otherPeriodEvent, type CrmTableShortcut } from '@/lib/crmTableShortcuts';
import { cn } from '@/lib/utils';
import type { CrmAttribute } from '@/types/crm';

interface CrmTableFiltersProps {
  /** Переключатель вида (только в обычном виде). */
  leading?: ReactNode;
  period: CrmTablePeriodState;
  /** KAN-905: "Только новые лиды"; без состояния переключателя нет. */
  newLeads?: NewLeadsOnlyState;
  leadFilters: CrmLeadFiltersState;
  url: CrmTableUrlFilters;
  attributes?: CrmAttribute[];
  attributeFilter: AttributeFilterValue | null;
  cities: string[];
  companyIdName: string | null;
  counts: Partial<Record<CrmTableShortcut, number | undefined>>;
  /** Кнопки быстрого выбора, недоступные пользователю. */
  hidden?: readonly CrmTableShortcut[];
  /** KAN-906: конец строки справа (число клиентов, "Развернуть"/"Свернуть"). */
  trailing?: ReactNode;
}

export function CrmTableFilters({
  leading,
  period,
  newLeads,
  leadFilters,
  url,
  attributes,
  attributeFilter,
  cities,
  companyIdName,
  counts,
  hidden,
  trailing,
}: CrmTableFiltersProps): JSX.Element {
  const narrow = useMediaQuery(NARROW_SCREEN_QUERY);
  const otherEvent = otherPeriodEvent(url.flags.event);

  const periodProps = {
    label: 'Период',
    choices: CRM_TABLE_PERIOD_CHOICES,
    range: period.range,
    onChange: period.setRange,
    onPreset: period.setPreset,
    onAllTime: period.setAllTime,
    testId: 'crm-period',
    compact: true,
  };
  const newLeadsToggle = newLeads && <NewLeadsToggle state={newLeads} />;
  const outcome = (
    <SegmentedControl
      label="Исход"
      options={CRM_TABLE_OUTCOME_OPTIONS}
      value={period.outcome}
      onChange={period.setOutcome}
      compact
    />
  );
  const sort = <CrmTableSortSelect value={period.sort} onChange={period.setSort} />;
  const shortcuts = (
    <CrmTableShortcuts active={period.shortcut} counts={counts} hidden={hidden} onToggle={period.toggleShortcut} />
  );
  const attribute = (
    <CrmAttributeFilter attributes={attributes} filter={attributeFilter} onChange={url.setAttribute} compact />
  );
  const location = (
    <CrmLocationFilters
      value={url.location}
      cities={cities}
      companyIdName={companyIdName}
      onChange={url.setLocation}
      onReset={url.resetLocation}
      compact
    />
  );
  const eventNote = otherEvent && (
    <div data-testid="crm-event-filter" className="flex items-center gap-2 text-sm text-gray-600">
      <span>
        Даты по событию: <span className="font-medium text-gray-900">{CRM_LEADS_EVENT_LABEL[otherEvent]}</span>
      </span>
      <button type="button" onClick={url.clearEvent} className="text-blue-600 hover:underline">
        по взятию в работу
      </button>
    </div>
  );

  if (narrow) {
    const active = crmTableActiveFilterCount({
      range: period.range,
      outcome: period.outcome,
      sort: period.sort,
      shortcut: period.shortcut,
      newLeads,
      hasAttribute: attributeFilter !== null,
      location: url.location,
      manager: leadFilters.filters.manager,
    });
    return (
      <div data-testid="crm-table-filters" className="space-y-2">
        <div data-testid="crm-filters-period-row" className="flex flex-wrap items-center gap-1.5">
          {leading}
          <CrmPeriodControl {...periodProps} />
        </div>
        <MobileFiltersPanel
          active={active}
          search={<CrmLeadFilters state={leadFilters} showManager={false} compact />}
          trailing={trailing}
        >
          {newLeadsToggle}
          {outcome}
          {sort}
          {shortcuts}
          {attribute}
          {location}
          <CrmLeadFilters state={leadFilters} showSearch={false} compact />
        </MobileFiltersPanel>
        {eventNote}
      </div>
    );
  }

  return (
    <div data-testid="crm-table-filters" className="space-y-2">
      <div className="flex flex-wrap items-center gap-1.5">
        {leading}
        <CrmPeriodControl {...periodProps} />
        {newLeadsToggle}
        {outcome}
        {sort}
        {shortcuts}
        {attribute}
        {location}
        <CrmLeadFilters state={leadFilters} compact />
        {trailing}
      </div>
      {eventNote}
    </div>
  );
}

interface MobileFiltersPanelProps {
  active: number;
  /** Поиск в строке рядом с кнопкой. */
  search: ReactNode;
  trailing?: ReactNode;
  /** Содержимое панели. */
  children: ReactNode;
}

/**
 * KAN-906: строка "поиск + Фильтры (значок - число активных) + trailing" и под ней
 * раскрывающаяся по кнопке панель с остальными фильтрами.
 */
function MobileFiltersPanel({ active, search, trailing, children }: MobileFiltersPanelProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  return (
    <>
      <div className="flex items-center gap-1.5">
        {search}
        <button
          type="button"
          data-testid="crm-filters-toggle"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => setOpen((v) => !v)}
          className={cn(
            'inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg border px-2.5 text-sm font-medium transition-colors',
            open || active > 0 ? 'border-blue-600 bg-blue-50 text-blue-800' : 'bg-white text-gray-700 hover:bg-gray-50'
          )}
        >
          <SlidersHorizontal className="h-4 w-4" aria-hidden="true" />
          Фильтры
          {active > 0 && (
            <span
              data-testid="crm-filters-count"

              className="rounded-full bg-blue-600 px-1.5 text-xs tabular-nums text-white"
            >
              {active}
            </span>
          )}
        </button>
        {trailing && <div className="flex shrink-0 items-center gap-1.5">{trailing}</div>}
      </div>
      {open && (
        <div
          id={panelId}
          data-testid="crm-filters-panel"
          role="region"
          aria-label="Фильтры"
          className="flex flex-wrap items-center gap-1.5 rounded-lg border bg-white p-2"
        >
          {children}
        </div>
      )}
    </>
  );
}
